/**
 * Search Results Component
 * 
 * Displays search results with message highlighting and jump-to functionality.
 */

import React from 'react';
import { List, Typography, Avatar, Tag, Button } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { Message } from '@municipality/types';

const { Text, Paragraph } = Typography;

interface SearchResult extends Message {
  conversationName?: string;
  conversationType?: 'direct' | 'group';
}

interface SearchResultsProps {
  results: SearchResult[];
  searchQuery: string;
  onJumpToMessage: (conversationId: string, messageId: string, messageDate?: Date) => void;
  onClose: () => void;
}

const ResultsContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
`;

const ResultItem = styled(List.Item)`
  padding: 16px !important;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #fafafa;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const ConversationName = styled(Text)`
  font-weight: 500;
  font-size: 14px;
`;

const MessageContent = styled(Paragraph)`
  margin: 0 !important;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  
  .highlight {
    background-color: #fffbe6;
    padding: 2px 4px;
    border-radius: 2px;
    font-weight: 500;
    color: #d48806;
  }
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #999;
`;

const ResultCount = styled.div`
  padding: 8px 16px;
  background-color: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #666;
`;

/**
 * Highlight search terms in text
 */
const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) {
    return text;
  }

  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={index} className="highlight">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  searchQuery,
  onJumpToMessage,
  onClose,
}) => {
  const { t } = useTranslation('chat');

  const handleResultClick = (result: SearchResult) => {
    // Pass the message date so we can load messages around it if needed
    onJumpToMessage(result.conversationId, result.id, result.createdAt);
    onClose();
  };

  return (
    <ResultsContainer>
      <ResultCount>
        {t('search.resultsCount', { count: results.length }) || `${results.length} result${results.length !== 1 ? 's' : ''} found`}
      </ResultCount>

      <List
        dataSource={results}
        renderItem={(result) => (
          <ResultItem onClick={() => handleResultClick(result)}>
            <List.Item.Meta
              avatar={
                <Avatar
                  src={result.senderAvatar}
                  icon={result.senderAvatar ? undefined : <UserOutlined />}
                >
                  {result.senderName?.[0]?.toUpperCase()}
                </Avatar>
              }
              title={
                <ResultHeader>
                  <ConversationName>
                    {result.conversationType === 'group' ? (
                      <TeamOutlined style={{ marginRight: 4 }} />
                    ) : (
                      <UserOutlined style={{ marginRight: 4 }} />
                    )}
                    {result.conversationName || 'Chat'}
                  </ConversationName>
                  {result.conversationType && (
                    <Tag color={result.conversationType === 'group' ? 'blue' : 'green'}>
                      {result.conversationType === 'group'
                        ? t('search.groupChat') || 'Group'
                        : t('search.directChat') || 'Direct'}
                    </Tag>
                  )}
                </ResultHeader>
              }
              description={
                <>
                  <MessageContent>
                    <Text strong style={{ fontSize: 14, fontWeight: 700, color: '#000' }}>
                      {result.senderName}
                    </Text>
                    {' • '}
                    {highlightText(result.content, searchQuery)}
                  </MessageContent>
                  <MessageMeta>
                    <span>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      {format(result.createdAt, 'dd/MM/yyyy HH:mm')}
                    </span>
                    {result.isEdited && (
                      <Tag color="orange">
                        {t('search.edited') || 'Edited'}
                      </Tag>
                    )}
                    <Button
                      type="link"
                      size="small"
                      icon={<ArrowRightOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResultClick(result);
                      }}
                    >
                      {t('search.jumpToMessage') || 'Jump to message'}
                    </Button>
                  </MessageMeta>
                </>
              }
            />
          </ResultItem>
        )}
      />
    </ResultsContainer>
  );
};

